import React from 'react';
import {
  Box,
  Heading,
  Text,
  Icon,
  Flex,
  Button,
  SimpleGrid,
  Image,
} from '@chakra-ui/react';
import { FaCrown, FaChartLine, FaExternalLinkAlt } from 'react-icons/fa';
import BaseBountyPool from './BaseBountyPool';
import { CASH_KING_CONFIG } from '../Games/ScratchCardConfigs';

/**
 * CashKingBountyPool Component
 * 
 * Custom bounty pool implementation for the Cash King scratch card game.
 * Extends the BaseBountyPool with Cash King specific styling and data.
 */
const CashKingBountyPool = () => {
  // Get bounty pool data from Cash King config
  const { bountyPool } = CASH_KING_CONFIG;
  
  // Cash King specific styling - using the exact same green colors as the card
  const cashKingStyles = {
    accentColor: "yellow.400",
    bgColor: "#1b4332", // Main dark green background matching the card
    glassBoxBg: "rgba(13, 59, 31, 0.7)", // Dark green with transparency
    glassBoxHighlightBg: "rgba(27, 67, 50, 0.7)", // Slightly lighter green
    glassBoxDarkBg: "rgba(9, 43, 22, 0.8)", // Darker green
  };
  
  // Custom glassmorphism styles for Cash King
  const customGlassStyles = {
    glassBox: {
      background: cashKingStyles.glassBoxBg,
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      border: "1px solid rgba(255, 255, 255, 0.18)",
      borderRadius: "16px",
    },
    glassBoxHighlight: {
      background: cashKingStyles.glassBoxHighlightBg,
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      border: "1px solid rgba(255, 255, 255, 0.18)",
      borderRadius: "16px",
    },
    glassBoxDark: {
      background: cashKingStyles.glassBoxDarkBg,
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      border: "1px solid rgba(255, 255, 255, 0.18)",
      borderRadius: "16px",
    }
  };

  // Cash King specific header
  const renderHeader = () => (
    <Flex align="center" mb={4}>
      <Icon as={FaCrown} boxSize={8} color="yellow.400" mr={3} />
      <Box>
        <Heading as="h3" size="lg" color="yellow.400">
          CASH KING Bounty Pool
        </Heading>
        <Text color="#4fd1c5" fontSize="sm">
          Win up to 100,000 JINU with the royal scratch card game!
        </Text>
      </Box>
    </Flex>
  );
  
  // Cash King specific footer with other games showcase
  const renderFooter = () => (
    <Box mt={8}>
      <Heading as="h4" size="md" mb={4} color="#4fd1c5">
        Ready to Try Your Luck?
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
        <Box 
          bg="black" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>QUICK CASH</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 50,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Play Now
          </Button>
        </Box>
        <Box 
          bg="purple.900" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>JACKPOT MILLIONS</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 250,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Play Now
          </Button>
        </Box>
        <Box 
          bg="blue.900" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>MEGA JINU SCRATCH</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 10,000,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Coming Soon
          </Button>
        </Box>
      </SimpleGrid>
    </Box>
  );
  
  return (
    <BaseBountyPool
      poolData={bountyPool}
      accentColor={cashKingStyles.accentColor}
      bgColor={cashKingStyles.bgColor}
      renderHeader={renderHeader}
      renderFooter={renderFooter}
      customGlassStyles={customGlassStyles}
      prizeColorMap={{
        '100K': 'yellow.400',
        '50K': 'yellow.300',
        '10K': 'green.300',
        '5K': 'green.200',
        '1K': 'teal.300',
        '100': 'teal.200'
      }}
      columnHeaders={{
        remaining: "REMAINING",
        odds: "ODDS",
        prize: "PRIZE"
      }}
    />
  );
};

export default CashKingBountyPool;
